'use client';

import { FC, useEffect, useRef, useState } from 'react';

import { Button, Modal, Notification, RadioCardGroup } from '@purpur/library';

import { CheckboxCard } from '@telialt/common-checkbox-card/src';
import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { NAVIGATION_MODAL_ZINDEX } from '@telialt/common-constants/src/modal';
import { RichTextContent } from '@telialt/common-rich-text-content/src/RichTextContent';
import { RichTextType } from '@telialt/models/src/CmsContent';
import { withParams } from '@telialt/utils/src/helpers';

import { Gift } from '../model';
import { Form, ModalBody } from './components/GiftSelection.styles';
import { PriceTag } from './components/PriceTag';
import { GIFT_SELECTION_TEST_ID } from './constants';
import { useGiftSelectionModalControls } from './hooks/useGiftSelectionModalControls';

type Props = {
    title: string;
    description?: RichTextType;
    gifts: Gift[];
    minimumGifts: number;
    onSelect: (gifts: string[]) => void;
    onCancel?: () => void;
};

export const GiftSelectionModal: FC<Props> = ({ gifts = [], minimumGifts, title, description, onSelect, onCancel }) => {
    const { t } = useTranslations();
    const { isGiftSelectionModalOpen, closeGiftSelectionModal } = useGiftSelectionModalControls();

    const firstGiftId = gifts.length >= 1 ? gifts[0].id : null;
    const defaultSelectedGifts = !!firstGiftId && minimumGifts === 1 ? [firstGiftId] : [];

    const [selectedGifts, setSelectedGifts] = useState(defaultSelectedGifts);

    useEffect(() => {
        setSelectedGifts(firstGiftId && minimumGifts === 1 ? [firstGiftId] : []);
    }, [firstGiftId, minimumGifts]);

    const formRef = useRef<HTMLFormElement>(null);

    const updateSelectedGifts = () => {
        setSelectedGifts(formRef.current ? ([...new FormData(formRef.current).values()] as string[]) : []);
    };

    const giftOptions = gifts.map((gift) => ({
        id: gift.id,
        value: gift.id,
        title: gift.title,
        image: gift.image?.src ? { src: gift.image.src, altText: gift.image.altText || '' } : undefined,
        body: <PriceTag price={gift.price} oldPrice={gift.oldPrice} />,
    }));

    const [isLoading, setIsLoading] = useState(false);
    const [wasSubmitFired, setWasSubmitFired] = useState(false);
    const canContinue = selectedGifts.length >= Math.min(minimumGifts, gifts.length);
    const errorMessage = withParams(t['plans.order.gift_selection.error.minimum_amount'], { amount: minimumGifts });
    const hasFormErrors = !canContinue && wasSubmitFired;

    const handleContinue = () => {
        setWasSubmitFired(true);
        if (canContinue) {
            setIsLoading(true);
            onSelect(selectedGifts);
        }
    };
    const handleCancel = () => {
        closeGiftSelectionModal();
        onCancel?.();
    };

    useEffect(() => {
        if (!isGiftSelectionModalOpen && isLoading) {
            setIsLoading(false);
        }
        if (!isGiftSelectionModalOpen) {
            setWasSubmitFired(false);
        }
    }, [isGiftSelectionModalOpen, isLoading]);

    return (
        <Modal open={isGiftSelectionModalOpen} onOpenChange={(open) => !open && handleCancel()}>
            <Modal.Content
                title={title}
                actions={
                    <>
                        <Button variant="primary" onClick={handleContinue} loading={isLoading}>
                            {t['plans.order.submit.button']}
                        </Button>
                        <Button variant="secondary" onClick={handleCancel}>
                            {t['common.modal.cancel']}
                        </Button>
                    </>
                }
                stickyButtons={true}
                closeButtonAriaLabel={t['common.modal.cancel']}
                showCloseButton={true}
                data-test={GIFT_SELECTION_TEST_ID.GIFT_SELECTION_MODAL}
                zIndex={NAVIGATION_MODAL_ZINDEX}
            >
                <ModalBody>
                    {!!description && <RichTextContent richText={description} />}
                    <Form ref={formRef} onChange={updateSelectedGifts}>
                        {(giftOptions.length === 1 && minimumGifts > 0 && (
                            <CheckboxCard
                                name="preview-selected"
                                id={giftOptions[0].id}
                                value={giftOptions[0].value}
                                title={giftOptions[0].title}
                                image={giftOptions[0].image}
                                body={giftOptions[0].body}
                                checked={true}
                                readOnly={true}
                                checkboxPosition="left"
                            />
                        )) ||
                            (minimumGifts === 1 && (
                                <RadioCardGroup
                                    id={GIFT_SELECTION_TEST_ID.GIFT_SELECTION_MODAL}
                                    name="select-one"
                                    radioPosition="left"
                                    required={true}
                                    defaultValue={giftOptions[0]?.value}
                                    items={giftOptions}
                                />
                            )) ||
                            ((minimumGifts === 0 || minimumGifts > 1) &&
                                giftOptions?.map((gift) => (
                                    <CheckboxCard
                                        key={gift.id}
                                        id={gift.id}
                                        value={gift.value}
                                        title={gift.title}
                                        image={gift.image}
                                        body={gift.body}
                                        name="select-multiple"
                                        checkboxPosition="left"
                                    />
                                )))}
                    </Form>
                    <Notification
                        open={hasFormErrors}
                        heading={errorMessage}
                        status="error"
                        role="alert"
                        aria-live="polite"
                    />
                </ModalBody>
            </Modal.Content>
        </Modal>
    );
};
